import { createLayerCanvas, resizeLayerCanvas } from "../layerCanvas";
import { createTextMeasurer } from "./textMeasure";

const DIGITS = "0123456789";

/**
 * Transparent Canvas 2D layer above the WebGL layer for all text (and,
 * as a separate instance, for the crosshair). Coordinates are CSS pixels;
 * the backing store follows devicePixelRatio, so text stays sharp after
 * resize and page zoom.
 *
 * @param {HTMLElement} container
 */
export const createTextLayer = (container) => {
  const canvas = createLayerCanvas(container);
  const context = canvas.getContext("2d");
  const measurer = createTextMeasurer(context);
  const centreOffsets = new Map();
  let pixelRatio = 1;

  // "middle" centres the em box, so digits sit low in their badges;
  // centre the glyphs of digits instead (caps have the same height)
  const centreOffset = (font) => {
    if (!centreOffsets.has(font)) {
      // The box is measured from the current baseline, so it must be the one drawText uses
      context.font = font;
      context.textBaseline = "alphabetic";
      const { actualBoundingBoxAscent, actualBoundingBoxDescent } = context.measureText(DIGITS);

      centreOffsets.set(font, (actualBoundingBoxAscent - actualBoundingBoxDescent) / 2);
    }

    return centreOffsets.get(font);
  };

  // Snaps a coordinate to the device pixel grid
  const snap = (value) => Math.round(value * pixelRatio) / pixelRatio;

  return {
    measure: measurer.measure,

    /** 2D context in CSS pixels between beginFrame calls, for shapes. */
    context,

    get pixelRatio() {
      return pixelRatio;
    },

    /** Drops cached widths and offsets, e.g. after the font has loaded. */
    resetMeasurements: () => {
      measurer.clear();
      centreOffsets.clear();
    },

    resize: (size) => {
      pixelRatio = size.pixelRatio;
      resizeLayerCanvas(canvas, size);
    },

    beginFrame: () => {
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    },

    /**
     * @param {string} text
     * @param {number} x
     * @param {number} y - vertical centre of the glyphs
     * @param {{ font: string, color: string, align: CanvasTextAlign, isSnappedX?: boolean }} style -
     *   isSnappedX false keeps x sub-pixel (time labels while the view follows time)
     */
    drawText: (text, x, y, { font, color, align, isSnappedX = true }) => {
      const baselineY = y + centreOffset(font);

      context.font = font;
      context.fillStyle = color;
      context.textAlign = align;
      context.textBaseline = "alphabetic";
      context.fillText(text, isSnappedX ? snap(x) : x, snap(baselineY));
    },

    destroy: () => canvas.remove(),
  };
};
